import { createContext, useContext, useReducer } from 'react';
import { useLocation, matchRoutes, RouteMatch, useResolvedPath } from 'react-router-dom';
import { useAppointmentStateUnsafe } from './../Appointment/appointment';
import type { GoMotoComponent } from '../../API/GoMoto.types';
import {
    SavedSessionDispatchTypes,
    SavedSessionCookieData,
    SavedSessionProviderProps,
    SavedSessionState,
    SavedSessionDispatch,
    SavedSessionDispatchContextType,
    SavedSessionStateContextType,
    SavedSessionReducerFunction,
    SavedSessionDispatchHook,
    SavedSessionStateHook,
} from './SavedSession.types';
import { IAppointment } from '../../API/GoMoto';

// #region Setup
/**
 * Defines the initial state object of the SavedSession context with all properties set to their base value.
 * This uses the SavedSessionCookieData because we declare the showButton prop directly in the provider
 * instantiation.
 */
const initialState: SavedSessionCookieData = {
    appointmentID: 0,
    appointmentKey: '',
    route: '',
};

/**
 * Sets up the SavedSession state context and defaults it to undefined so that if any SavedSession context hook is used outside of the
 * SavedSession state context, we can throw an error.
 */
const SavedSessionStateContext: SavedSessionStateContextType = createContext<SavedSessionState>(undefined);

/**
 * Sets up the SavedSession dispatch context and defaults it to undefined so that if any SavedSession hook that uses the dispatch context is
 * used outside of the SavedSession dispatch context, we can throw an error.
 */
const SavedSessionDispatchContext: SavedSessionDispatchContextType = createContext<SavedSessionDispatch>(undefined);
// #endregion

// #region Reducer
/**
 * Handles a single action dispatch to the SavedSession context to update the internal state
 * of the context.
 *
 * @param state - {@link SavedSessionReducerFunction state} description.
 * @param action - {@link SavedSessionReducerFunction action} description.
 */
export const savedSessionReducer: SavedSessionReducerFunction = (state, action) => {
    switch (action.type) {
        case SavedSessionDispatchTypes.Update:
            return {
                ...state,
                ...action.payload,
            };
        case SavedSessionDispatchTypes.ShowSaveButton:
            return {
                ...state,
                showSaveButton: action.payload,
            };
        default:
            return state;
    }
};
// #endregion

// #region Provider
/**
 * Provides the SavedSession state and a dispatch to update the state to all components that
 * are rendered as a child node of this component.
 *
 * @param children - {@link SavedSessionProviderProps children} description.
 */
export const SavedSessionProvider: GoMotoComponent<SavedSessionProviderProps> = ({ children }) => {
    const location = useLocation();
    const { pathname } = useResolvedPath('.');
    const appointment: IAppointment | undefined = useAppointmentStateUnsafe();
    const [state, dispatch] = useReducer<SavedSessionReducerFunction>(savedSessionReducer, initialState);
    const enableSaveSessionRoutes: RouteMatch[] | null = matchRoutes(
        [
            {
                path: `${pathname}appointment/services-previously-added`,
            },
            {
                path: `${pathname}appointment/recalls`,
            },
            {
                path: `${pathname}appointment/valuation`,
            },
            {
                path: `${pathname}appointment/hunter`,
            },
            {
                path: `${pathname}appointment/additional-services`,
            },
            {
                path: `${pathname}appointment/select-accessories`,
            },
            {
                path: `${pathname}appointment/xtime`,
            },
            {
                path: `${pathname}appointment/services`,
            },
            {
                path: `${pathname}appointment/services/add`,
            },
            {
                path: `${pathname}appointment/services/comments`,
            },
            {
                path: `${pathname}appointment/transportation`,
            },
            {
                path: `${pathname}appointment/transportation/pickup`,
            },
            {
                path: `${pathname}appointment/summary`,
            },
        ],
        location,
    );
    const enableSaveSession: boolean = !!enableSaveSessionRoutes && enableSaveSessionRoutes.length > 0;

    return (
        <SavedSessionStateContext.Provider
            value={{
                ...state,
                showSaveButton: !!enableSaveSession && !!appointment?.key,
            }}
        >
            <SavedSessionDispatchContext.Provider value={dispatch}>
                {children}
            </SavedSessionDispatchContext.Provider>
        </SavedSessionStateContext.Provider>
    );
};
// #endregion

// #region State Hook
/**
 * SavedSession context hook that retrieves the state from the context, or throws
 * an error if not used in a component that is a child of the SavedSession context.
 */
export const useSavedSessionState: SavedSessionStateHook = () => {
    const context: SavedSessionState = useContext(SavedSessionStateContext);

    if (context === undefined) {
        throw new Error('useSavedSessionState must be used within a SavedSessionProvider');
    }

    return context;
};
// #endregion

// #region Dispatch Hook
/**
 * SavedSession context hook that returns a dispatch function which can dispatch actions to
 * update the state of the SavedSession context, or throws an error if not used in a
 * component that is a child of the SavedSession context.
 */
export const useSavedSessionDispatch: SavedSessionDispatchHook = () => {
    const context: SavedSessionDispatch = useContext(SavedSessionDispatchContext);

    if (context === undefined) {
        throw new Error('useSavedSessionDispatch must be used within a SavedSessionProvider');
    }

    return context;
};
// #endregion